import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/components/ui/status";
import { INVOICES_LIST_COPY } from "./copy";

/**
 * The summary row of the receivables list (design frame INV-00).
 *
 * The design spec is explicit about what a tile may be: *"Every tile names its
 * source and the timestamp it was computed; a source that is unavailable shows
 * unknown, never zero."* This component enforces that shape rather than trusting
 * each caller — `source` and `computedAt` are required props, so a tile cannot
 * be added without saying where its figure came from and when.
 *
 * It renders no figure of its own. The page builds each `value` from the
 * contract's response (a count of a status the service returned) or passes the
 * shared `UnavailableValue` marker when nothing carries the figure. There is no
 * default, no zero, and no arithmetic here.
 *
 * `lowerBound` is the "N or more" rule the transfers list already uses: when the
 * service says another page exists, a count over the loaded rows is a floor, and
 * the tile says so rather than presenting a partial count as a total.
 */

export interface ReceivablesTile {
  /** Stable identity for React and for tests. */
  key: string;
  label: string;
  /** The figure, or the shared unavailable marker. Never a placeholder number. */
  value: ReactNode;
  /** The route the figure was read from, or why no route carries it. */
  source: string;
  /** When the figure was computed (`HH:MM`), or null when there is none to time. */
  computedAt: string | null;
  /** The count is a floor because the service has more pages. */
  lowerBound?: boolean;
  /** Accessible text for the lower-bound marker. */
  lowerBoundTitle?: string;
  tone?: Tone;
}

const VALUE_TONE: Record<Tone, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  pending: "text-pending",
  neutral: "text-ink",
};

export function ReceivablesSummary({ tiles }: { tiles: ReceivablesTile[] }) {
  if (tiles.length === 0) return null;
  return (
    <div
      className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-5"
      role="group"
      aria-label={INVOICES_LIST_COPY.summary.groupLabel}
    >
      {tiles.map((tile) => (
        <div
          key={tile.key}
          data-testid={`receivables-tile-${tile.key}`}
          className="flex flex-col rounded-lg border border-line bg-surface px-5 py-4"
        >
          <div className={cn("text-[26px] font-bold leading-[1.1] tracking-tight", VALUE_TONE[tile.tone ?? "neutral"])}>
            {tile.value}
            {tile.lowerBound && (
              <span
                className="ml-0.5 text-base font-semibold text-muted"
                aria-label={tile.lowerBoundTitle}
                title={tile.lowerBoundTitle}
              >
                +
              </span>
            )}
          </div>
          <div className="mt-1 text-xs font-semibold text-ink">{tile.label}</div>
          {/* The source line is not decoration: a figure with no named origin is
              the shape of bug this page exists to avoid. */}
          <div className="mt-1.5 text-[11px] leading-snug text-muted">
            {tile.source}
            {tile.computedAt ? ` · ${tile.computedAt}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}
